import { PaperclipIcon } from 'lucide-react'

import { cn, formatBytes } from '@/utils'
import type { SupportAttachment, SupportMessage } from '../types'
import { firstLinkIn, LinkedText } from './LinkedText'
import { LinkPreviewCard } from './LinkPreviewCard'

const isImage = (attachment: SupportAttachment) =>
  attachment.contentType.startsWith('image/')

const AttachmentPreview = ({
  attachment,
}: {
  attachment: SupportAttachment
}) => {
  if (isImage(attachment)) {
    return (
      <a
        href={attachment.url}
        target="_blank"
        rel="noreferrer"
        className="block cursor-pointer"
        title={attachment.filename}
      >
        <img
          src={attachment.url}
          alt={attachment.filename}
          className="max-h-40 max-w-full rounded-lg border border-border object-contain"
        />
      </a>
    )
  }

  return (
    <a
      href={attachment.url}
      target="_blank"
      rel="noreferrer"
      className="inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-border bg-white px-2.5 py-1 text-xs text-blue-7 transition-colors hover:bg-blue-1"
    >
      <PaperclipIcon className="h-3 w-3 shrink-0" />
      <span className="max-w-40 truncate">{attachment.filename}</span>
      <span className="text-ink-subtle">
        {formatBytes(attachment.byteSize)}
      </span>
    </a>
  )
}

// The author and the time live on the group header, so the bubble only carries
// what was said.
export const TicketMessageBubble = ({
  message,
  showsLinkPreview,
}: {
  message: SupportMessage
  showsLinkPreview: boolean
}) => {
  const isUser = message.authorType === 'user'
  const previewUrl = showsLinkPreview ? firstLinkIn(message.body) : null

  return (
    <div
      className={cn(
        'max-w-full rounded-2xl px-4 py-3 text-sm leading-relaxed',
        isUser
          ? 'bg-brand-deep text-white'
          : 'border border-border bg-white text-blue-7 shadow-sm',
      )}
    >
      <p className="whitespace-pre-wrap break-words">
        <LinkedText text={message.body} />
      </p>

      {previewUrl && <LinkPreviewCard key={previewUrl} url={previewUrl} />}

      {message.attachments.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {message.attachments.map((attachment) => (
            <AttachmentPreview key={attachment.id} attachment={attachment} />
          ))}
        </div>
      )}
    </div>
  )
}
